import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Crown,
  FileText,
  Headphones,
  Loader2,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import { SubscriptionTier } from '../types';
import { ApiService } from '../services/api';
import { BETA_ANNUAL_DISCOUNT_PERCENT, BillingCycle, formatCurrency, getBetaAnnualSavings, getPlanConfig, getStandardAnnualFee, getVisiblePlans, normalizePlanId } from '../config/pricing';

type PlanVariant = 'standard' | 'recommended' | 'enterprise';

const planVariant = (id: string): PlanVariant => {
  if (id === 'growth') return 'recommended';
  if (id === 'concierge') return 'enterprise';
  return 'standard';
};

type FeatureKey =
  | 'unlimitedPcns'
  | 'fiveAssistants'
  | 'perPcnSummary'
  | 'monthlyReport'
  | 'repeatFlags'
  | 'recoveryTracking'
  | 'hireStorage'
  | 'evidencePacks'
  | 'legalPdf'
  | 'submissionLog'
  | 'auditTrail'
  | 'driverRisk'
  | 'roleAccess'
  | 'bulkActions'
  | 'multiBranch'
  | 'apiAccess'
  | 'accountContact'
  | 'managedOnboarding';

const featureLabels: Record<FeatureKey, string> = {
  unlimitedPcns: 'Unlimited PCNs',
  fiveAssistants: 'All five AI assistants',
  perPcnSummary: 'Per-PCN AI summary',
  monthlyReport: 'Monthly aggregate report',
  repeatFlags: 'Repeat driver/location flags',
  recoveryTracking: 'Recovery rate tracking',
  hireStorage: 'Hire agreement storage',
  evidencePacks: 'Evidence pack generation',
  legalPdf: 'Legal-ready PDF export',
  submissionLog: 'Proof-of-submission log',
  auditTrail: 'Audit trail',
  driverRisk: 'Driver risk scoring',
  roleAccess: 'Role-based access',
  bulkActions: 'Bulk PCN actions',
  multiBranch: 'Multi-branch controls',
  apiAccess: 'API access',
  accountContact: 'Dedicated account contact',
  managedOnboarding: 'Managed onboarding',
};

const planFeatureMatrix: Record<string, Record<FeatureKey, boolean | string>> = {
  starter: {
    unlimitedPcns: true,
    fiveAssistants: true,
    perPcnSummary: true,
    monthlyReport: false,
    repeatFlags: false,
    recoveryTracking: false,
    hireStorage: false,
    evidencePacks: false,
    legalPdf: false,
    submissionLog: false,
    auditTrail: false,
    driverRisk: false,
    roleAccess: false,
    bulkActions: false,
    multiBranch: false,
    apiAccess: false,
    accountContact: false,
    managedOnboarding: false,
  },
  growth: {
    unlimitedPcns: true,
    fiveAssistants: true,
    perPcnSummary: true,
    monthlyReport: true,
    repeatFlags: true,
    recoveryTracking: true,
    hireStorage: true,
    evidencePacks: false,
    legalPdf: false,
    submissionLog: false,
    auditTrail: false,
    driverRisk: false,
    roleAccess: false,
    bulkActions: false,
    multiBranch: false,
    apiAccess: false,
    accountContact: false,
    managedOnboarding: false,
  },
  pro_fleet: {
    unlimitedPcns: true,
    fiveAssistants: true,
    perPcnSummary: true,
    monthlyReport: true,
    repeatFlags: true,
    recoveryTracking: true,
    hireStorage: true,
    evidencePacks: true,
    legalPdf: true,
    submissionLog: true,
    auditTrail: true,
    driverRisk: true,
    roleAccess: true,
    bulkActions: true,
    multiBranch: false,
    apiAccess: false,
    accountContact: false,
    managedOnboarding: false,
  },
  concierge: {
    unlimitedPcns: true,
    fiveAssistants: true,
    perPcnSummary: true,
    monthlyReport: true,
    repeatFlags: true,
    recoveryTracking: true,
    hireStorage: true,
    evidencePacks: true,
    legalPdf: true,
    submissionLog: true,
    auditTrail: true,
    driverRisk: true,
    roleAccess: true,
    bulkActions: true,
    multiBranch: true,
    apiAccess: 'Phase 3',
    accountContact: true,
    managedOnboarding: true,
  },
};

const supportByPlan: Record<string, string> = {
  starter: 'Email support',
  growth: 'Priority support + onboarding call',
  pro_fleet: 'Priority support, dedicated contact, quarterly check-in',
  concierge: 'Custom SLA',
};

const cardFeatureGroups: Record<string, { label: string; features: FeatureKey[] }> = {
  starter: {
    label: 'Core FleetMode included',
    features: ['unlimitedPcns', 'fiveAssistants', 'perPcnSummary'],
  },
  growth: {
    label: 'Everything in Starter, plus',
    features: ['monthlyReport', 'repeatFlags', 'recoveryTracking', 'hireStorage'],
  },
  pro_fleet: {
    label: 'Everything in Growth, plus',
    features: ['evidencePacks', 'legalPdf', 'submissionLog', 'auditTrail', 'driverRisk', 'roleAccess', 'bulkActions'],
  },
  concierge: {
    label: 'Everything in Pro Fleet, plus',
    features: ['multiBranch', 'apiAccess', 'accountContact', 'managedOnboarding'],
  },
};

const Pricing: React.FC = () => {
  const { addToast } = useToast();
  const { tier } = useSubscription();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<SubscriptionTier | null>(null);
  const [monthlyPcns, setMonthlyPcns] = useState(60);
  const [adminFee, setAdminFee] = useState(20);

  const activePlanId = tier === 'trial' ? 'trial' : normalizePlanId(tier);
  const plans = getVisiblePlans();

  useEffect(() => {
    void ApiService.recordFleetModeLeadEvent('pricing_viewed', 'pricing', {
      active_plan: activePlanId,
      billing_cycle: billingCycle,
      monthly_pcns: monthlyPcns,
      admin_fee: adminFee,
    }).catch(() => {
      // Commercial signal tracking must not block pricing page access.
    });
  }, []);

  const roi = useMemo(() => {
    const pcns = Math.max(0, Math.floor(monthlyPcns || 0));
    const fee = Math.max(0, Math.floor(adminFee || 0));
    const workflowValue = pcns * fee;
    const growthFee = getPlanConfig('growth').monthlyFee ?? 179;
    const planShare = workflowValue > 0 ? Math.round((growthFee / workflowValue) * 100) : 0;
    return { pcns, fee, workflowValue, growthFee, planShare };
  }, [monthlyPcns, adminFee]);

  const handleCta = async (planId: SubscriptionTier) => {
    const normalized = normalizePlanId(planId);

    if (activePlanId === normalized) {
      addToast('You are already on this plan.', 'info');
      return;
    }

    void ApiService.recordFleetModeLeadEvent('plan_selected', 'pricing', {
      selected_plan: normalized,
      active_plan: activePlanId,
      billing_cycle: billingCycle,
      monthly_pcns: monthlyPcns,
      admin_fee: adminFee,
    }).catch(() => {
      // Do not block user upgrade flow if internal signal tracking fails.
    });

    if (normalized === 'concierge') {
      void ApiService.recordFleetModeLeadEvent('upgrade_clicked', 'pricing', {
        selected_plan: normalized,
        active_plan: activePlanId,
        billing_cycle: billingCycle,
        channel: 'mailto',
      }).catch(() => {});
      window.location.href = 'mailto:support@pcnguard.co.uk?subject=FleetMode%20Concierge%20plan%20review';
      return;
    }

    setLoadingPlan(normalized);
    try {
      await ApiService.recordFleetModeLeadEvent('upgrade_clicked', 'pricing', {
        selected_plan: normalized,
        active_plan: activePlanId,
        billing_cycle: billingCycle,
        channel: 'stripe',
      }).catch(() => {});
      const { url } = await ApiService.createCheckoutSession(normalized, billingCycle);
      if (!url) throw new Error('Missing checkout URL');
      await ApiService.recordFleetModeLeadEvent('payment_started', 'pricing', {
        selected_plan: normalized,
        active_plan: activePlanId,
        billing_cycle: billingCycle,
      }).catch(() => {});
      addToast('Opening secure Stripe Checkout...', 'info');
      window.location.href = url;
    } catch (err) {
      console.error(err);
      addToast('Could not start checkout. Please try again.', 'error');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/30 dark:text-primary-300">
              <ShieldCheck size={14} />
              FleetMode plans
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
              Choose the plan that matches your PCN operation.
            </h1>
            <p className="mt-3 text-base leading-7 text-slate-600 dark:text-slate-300">
              Paid FleetMode plans include unlimited PCNs. Choose by team seats, reporting depth,
              evidence tools, support level, and whether your operation needs managed onboarding.
            </p>
          </div>

          <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40 lg:max-w-sm">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
              <TrendingUp size={18} className="text-primary-600" />
              ROI anchor
            </div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              A fleet processing <strong>{roi.pcns} PCNs/month</strong> with a <strong>{formatCurrency(roi.fee)}</strong> admin fee has
              {' '}<strong>{formatCurrency(roi.workflowValue)}</strong> moving through the workflow monthly.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                PCNs / month
                <input
                  type="number"
                  min={0}
                  value={monthlyPcns}
                  onChange={(e) => setMonthlyPcns(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Admin fee
                <input
                  type="number"
                  min={0}
                  value={adminFee}
                  onChange={(e) => setAdminFee(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
            </div>
            <div className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              Growth at {formatCurrency(roi.growthFee)}/month is about {roi.planShare || 0}% of that tracked workflow value.
            </div>
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900 dark:text-white">Billing cycle</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Beta annual lock-in gives early customers {BETA_ANNUAL_DISCOUNT_PERCENT}% off the first year when they choose yearly.
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-950/40">
          {(['monthly', 'yearly'] as BillingCycle[]).map((cycle) => (
            <button
              key={cycle}
              onClick={() => setBillingCycle(cycle)}
              className={`rounded-md px-4 py-2 text-sm font-semibold capitalize transition ${
                billingCycle === cycle
                  ? 'bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950'
                  : 'text-slate-600 hover:text-slate-950 dark:text-slate-300 dark:hover:text-white'
              }`}
            >
              {cycle}
            </button>
          ))}
        </div>
      </div>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-4">
        {plans.map((plan) => {
          const variant = planVariant(plan.id);
          const isCurrent = activePlanId === plan.id;
          const price = billingCycle === 'monthly' ? plan.monthlyFee : plan.yearlyFee;
          const standardAnnualFee = getStandardAnnualFee(plan.id);
          const annualSavings = getBetaAnnualSavings(plan.id);
          const isLoading = loadingPlan === plan.id;
          const cardTone = isCurrent
            ? 'border-emerald-400 bg-slate-950 text-white shadow-lg shadow-emerald-950/20 ring-1 ring-emerald-300/40'
            : variant === 'recommended'
              ? 'border-primary-500 bg-white text-slate-950 shadow-lg shadow-primary-950/10 ring-1 ring-primary-200 dark:bg-slate-900 dark:text-white dark:ring-primary-900/50'
              : variant === 'enterprise'
                ? 'border-slate-900 bg-slate-950 text-white dark:border-slate-700'
                : 'border-slate-200 bg-white text-slate-950 dark:border-slate-800 dark:bg-slate-900 dark:text-white';
          const mutedText = isCurrent || variant === 'enterprise' ? 'text-slate-300' : 'text-slate-600 dark:text-slate-300';
          const softText = isCurrent || variant === 'enterprise' ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400';
          const strongText = isCurrent || variant === 'enterprise' ? 'text-white' : 'text-slate-950 dark:text-white';
          const panelTone = isCurrent || variant === 'enterprise'
            ? 'border-white/10 bg-white/[0.06]'
            : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40';

          return (
            <article
              key={plan.id}
              className={`relative flex min-h-[560px] flex-col rounded-lg border p-5 shadow-sm ${cardTone}`}
            >
              {variant === 'recommended' && (
                <div className="absolute -top-3 left-6 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700 shadow-sm">
                  Recommended
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className={`text-lg font-bold ${strongText}`}>{plan.shortName}</h2>
                  <p className={`mt-2 text-sm leading-6 ${mutedText}`}>
                    {plan.description}
                  </p>
                </div>
                {isCurrent && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">
                    <CheckCircle2 size={13} />
                    Active
                  </span>
                )}
              </div>

              <div className="mt-5">
                <div className="flex items-end gap-1">
                  <span className={`text-3xl font-bold ${strongText}`}>{price === null ? 'Custom' : formatCurrency(price)}</span>
                  {price !== null && <span className={`pb-1 text-xs font-semibold ${softText}`}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>}
                </div>
                <p className={`mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold ${isCurrent || variant === 'enterprise' ? 'text-slate-200' : 'text-slate-700 dark:text-slate-200'}`}>
                  <Users size={14} />
                  <span>{plan.seats}</span>
                  <span className={softText}>·</span>
                  <span>Unlimited PCNs</span>
                  <span className={softText}>·</span>
                  <span>All AI assistants</span>
                </p>
              </div>

              {price !== null && billingCycle === 'yearly' && annualSavings !== null && standardAnnualFee !== null && (
                <div className="mt-4 rounded-lg border border-slate-700 bg-slate-950 p-3 text-white shadow-sm">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-300">
                    Beta annual lock-in
                  </div>
                  <div className="mt-1 text-sm font-bold text-white">
                    Save {formatCurrency(annualSavings)} in year 1
                  </div>
                  <div className="mt-1 text-[11px] leading-5 text-slate-300">
                    Normally <span className="line-through">{formatCurrency(standardAnnualFee)}/yr</span> with {BETA_ANNUAL_DISCOUNT_PERCENT}% beta discount applied.
                  </div>
                </div>
              )}

              <div className={`mt-4 rounded-lg border p-4 ${panelTone}`}>
                <div className={`text-xs font-bold uppercase tracking-wide ${softText}`}>
                  {cardFeatureGroups[plan.id].label}
                </div>
                <div className={`mt-2 flex items-center gap-2 text-xs ${softText}`}>
                  <FileText size={14} />
                  {supportByPlan[plan.id]}
                </div>
                <ul className="mt-4 flex-1 space-y-2.5">
                  {cardFeatureGroups[plan.id].features
                    .filter((feature) => Boolean(planFeatureMatrix[plan.id][feature]))
                    .map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isCurrent ? 'text-emerald-300' : variant === 'enterprise' ? 'text-amber-300' : 'text-primary-600'}`} />
                      <span className={isCurrent || variant === 'enterprise' ? 'text-slate-200' : 'text-slate-700 dark:text-slate-300'}>
                        {featureLabels[feature]}
                        {typeof planFeatureMatrix[plan.id][feature] === 'string' ? ` (${planFeatureMatrix[plan.id][feature]})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => handleCta(plan.id)}
                disabled={isCurrent || isLoading}
                className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition ${
                  isCurrent
                    ? 'cursor-default bg-slate-800 text-slate-300 ring-1 ring-white/10'
                    : variant === 'recommended'
                      ? 'bg-primary-700 text-white shadow-sm hover:bg-primary-800 dark:bg-primary-500 dark:text-slate-950 dark:hover:bg-primary-400'
                      : variant === 'enterprise'
                        ? 'bg-amber-300 text-slate-950 hover:bg-amber-200'
                        : 'bg-slate-950 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200'
                }`}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : variant === 'enterprise' ? <Headphones className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                {isCurrent ? 'Current plan' : variant === 'enterprise' ? 'Book plan review' : 'Choose plan'}
              </button>
            </article>
          );
        })}
      </section>

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900/50">
        <div className="grid gap-5 md:grid-cols-3">
          <div className="flex gap-3">
            <Crown className="mt-1 h-5 w-5 shrink-0 text-primary-600" />
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white">Team access after upgrade</h3>
              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">Trial workspaces stay single-seat. Paid tiers unlock the team seats shown above.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-primary-600" />
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white">AI coverage is included</h3>
              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">All paid plans include the five AI assistants and per-PCN summaries.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Headphones className="mt-1 h-5 w-5 shrink-0 text-primary-600" />
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white">Billing and upgrades stay together</h3>
              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">Billing shows the current subscription and the next plan choices, so teams do not have to jump between separate plan screens.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
